let activeStream: MediaStream | null = null;

interface PostResponse {
  ok: boolean;
  path?: string;
  relativePath?: string;
  error?: string;
}

interface Rect { x: number; y: number; w: number; h: number }

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]!) : null;
}

async function ensureStream(): Promise<MediaStream> {
  if (activeStream) {
    const track = activeStream.getVideoTracks()[0];
    if (track && track.readyState === "live") return activeStream;
  }
  // preferCurrentTab is non-standard but supported by Chromium-based browsers.
  // Falls back gracefully on Firefox (user just sees the standard share dialog).
  // The cast to MediaStreamConstraints silences TS — types haven't caught up.
  const constraints = {
    video: { displaySurface: "browser" },
    preferCurrentTab: true,
    audio: false,
  } as MediaStreamConstraints;
  activeStream = await navigator.mediaDevices.getDisplayMedia(constraints);
  // When the user clicks "Stop sharing" in the browser banner, mark stream
  // dead so the next capture re-prompts.
  const track = activeStream.getVideoTracks()[0];
  if (track) {
    track.addEventListener("ended", () => { activeStream = null; });
  }
  return activeStream;
}

function pickRectangleViaOverlay(): Promise<Rect | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.id = "screenshot-overlay";
    overlay.innerHTML = '<div class="screenshot-rubberband" hidden></div><div class="screenshot-hint">Bereich markieren — ESC zum Abbrechen</div>';
    document.body.appendChild(overlay);

    const band = overlay.querySelector(".screenshot-rubberband") as HTMLElement;

    let startX = 0, startY = 0, dragging = false;

    const cleanup = (): void => {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        cleanup();
        resolve(null);
      }
    };

    overlay.addEventListener("pointerdown", (e) => {
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      band.style.left = `${startX}px`;
      band.style.top = `${startY}px`;
      band.style.width = "0px";
      band.style.height = "0px";
      band.hidden = false;
      overlay.setPointerCapture(e.pointerId);
    });
    overlay.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(e.clientX - startX);
      const h = Math.abs(e.clientY - startY);
      band.style.left = `${x}px`;
      band.style.top = `${y}px`;
      band.style.width = `${w}px`;
      band.style.height = `${h}px`;
    });
    overlay.addEventListener("pointerup", (e) => {
      if (!dragging) return;
      dragging = false;
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(e.clientX - startX);
      const h = Math.abs(e.clientY - startY);
      cleanup();
      if (w < 10 || h < 10) {
        resolve(null);
        return;
      }
      resolve({ x, y, w, h });
    });
    // pointercancel fires when the browser hijacks the gesture (touch/stylus
    // edge cases, navigation gestures). Without this, dragging state would
    // leak and the overlay would stay on screen forever.
    overlay.addEventListener("pointercancel", () => {
      if (!dragging) return;
      dragging = false;
      cleanup();
      resolve(null);
    });

    document.addEventListener("keydown", onKey);
  });
}

async function captureCroppedPng(stream: MediaStream, rect: Rect): Promise<Blob> {
  // ImageCapture API isn't widely supported yet — fall back to playing the
  // stream into a hidden <video> and drawing it to canvas.
  const video = document.createElement("video");
  video.srcObject = stream;
  video.muted = true;
  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error("video failed to load stream"));
  });
  await video.play();

  // Wait one frame so the video has rendered something
  await new Promise((res) => requestAnimationFrame(res));

  const dpr = window.devicePixelRatio || 1;
  // Map CSS-pixel rect → device-pixel rect. The MediaStream's frame size
  // matches the BROWSER VIEWPORT in device pixels (e.g. 2560×1440 on Retina
  // 1280×720 logical). devicePixelRatio bridges that gap.
  const sx = Math.round(rect.x * dpr);
  const sy = Math.round(rect.y * dpr);
  const sw = Math.round(rect.w * dpr);
  const sh = Math.round(rect.h * dpr);

  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d context unavailable");
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);

  // Free the video element + its stream reference (the stream itself stays
  // alive because activeStream still holds a reference)
  video.pause();
  video.srcObject = null;

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error("toBlob failed")); return; }
      resolve(blob);
    }, "image/png");
  });
}

function showToast(thumbBlob: Blob, relativePath: string): void {
  const root = document.getElementById("modal-root");
  if (!root) return;
  const tpl = document.getElementById("tpl-screenshot-toast") as HTMLTemplateElement | null;
  if (!tpl) {
    showError("Toast-Template fehlt — bitte Seite neu laden.");
    return;
  }
  const node = tpl.content.cloneNode(true) as DocumentFragment;
  const toast = node.firstElementChild as HTMLElement;
  const img = toast.querySelector("img") as HTMLImageElement;
  const pathEl = toast.querySelector(".screenshot-toast-path") as HTMLElement;
  const copyBtn = toast.querySelector(".screenshot-toast-copy") as HTMLButtonElement;
  const closeBtn = toast.querySelector(".screenshot-toast-close") as HTMLButtonElement;

  img.src = URL.createObjectURL(thumbBlob);
  pathEl.textContent = relativePath;

  copyBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(relativePath);
      copyBtn.textContent = "✓ kopiert";
      setTimeout(() => { copyBtn.textContent = "📋 Pfad kopieren"; }, 1500);
    } catch {
      copyBtn.textContent = "Fehler";
    }
  });
  closeBtn.addEventListener("click", () => {
    URL.revokeObjectURL(img.src);
    toast.remove();
  });

  root.appendChild(toast);
}

function showError(msg: string): void {
  // Reuse the toast slot for errors with an error class
  const root = document.getElementById("modal-root");
  if (!root) return;
  const div = document.createElement("div");
  div.className = "screenshot-toast screenshot-toast-error";
  div.innerHTML = '<button class="screenshot-toast-close" type="button" title="Schließen">×</button><div class="screenshot-toast-body"><p class="screenshot-toast-msg"></p></div>';
  (div.querySelector(".screenshot-toast-msg") as HTMLElement).textContent = msg;
  (div.querySelector(".screenshot-toast-close") as HTMLButtonElement).addEventListener("click", () => div.remove());
  root.appendChild(div);
  setTimeout(() => div.remove(), 6000);
}

async function captureForSlug(slug: string): Promise<void> {
  let stream: MediaStream;
  try {
    stream = await ensureStream();
  } catch (e) {
    showError("Screen-Sharing wurde abgelehnt — bitte erneut klicken und 'Tab teilen' wählen.");
    return;
  }
  const rect = await pickRectangleViaOverlay();
  if (!rect) return;

  let blob: Blob;
  try {
    blob = await captureCroppedPng(stream, rect);
  } catch (e) {
    showError(`Capture fehlgeschlagen: ${(e as Error).message}`);
    return;
  }

  let res: Response;
  try {
    res = await fetch(`/api/projects/${slug}/screenshots`, {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-CSRF-Token": getCookie("csrf_token") ?? "",
      },
      body: blob,
    });
  } catch (e) {
    showError(`Upload fehlgeschlagen: ${(e as Error).message}`);
    return;
  }

  let data: PostResponse | null = null;
  try { data = await res.json() as PostResponse; } catch { /* ignore */ }

  if (res.status === 200 && data?.ok && data.relativePath) {
    showToast(blob, data.relativePath);
  } else {
    showError(`Server: ${data?.error ?? `HTTP ${res.status}`}`);
  }
}

export function setupScreenshotButton(getSlug: () => string | null): void {
  const btn = document.getElementById("screenshot-btn") as HTMLButtonElement | null;
  if (!btn) return;
  btn.addEventListener("click", async () => {
    const slug = getSlug();
    if (!slug) {
      showError("Kein Projekt ausgewählt.");
      return;
    }
    btn.disabled = true;
    try {
      await captureForSlug(slug);
    } finally {
      btn.disabled = false;
    }
  });
}
